import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import DropZone from "../components/DropZone";
import SortableImages from "../components/SortableImages";
import ContentBlocksEditor from "../components/ContentBlocksEditor";
import TranslationTabs from "../components/TranslationTabs";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Edit, Trash2, Save, ArrowLeft, GripVertical, X, Eye, EyeOff, Palette, Wrench } from "lucide-react";
import { Editor as TinyEditor } from "@tinymce/tinymce-react";

const EMPTY_CASE = {
  slug: "", title: "", concept: "", developer: "", director: "", type: "", year: new Date().getFullYear(),
  city: "", state: "", country: "Brasil", typologies: "", sizes: "", period: "",
  vgv_estimado: "",
  categories: [], services: [], featured: false,
  verticals: [], padrao: "",
  main_team: [],
  cover: "", cover_alt: "", gallery: [], video_url: "",
  situation: "", solution: "", strategy: "", deliveries: "", results: "",
  section_styles: {
    situation_color: "#FF5E00", solution_color: "#FF5E00",
    strategy_color: "#FF5E00", deliveries_color: "#FF5E00",
    results_color: "#FF5E00", deliveries_bullet_color: "#FF5E00",
  },
  quote: { enabled: false, text: "", author: "", role: "", avatar: "" },
  content_blocks: [],
  translations: { en: {}, es: {} },
  meta_title: "", meta_description: "", main_keyword: "", secondary_keywords: [], og_image: "", noindex: false,
};

/** Fields exposed on the EN/ES tabs of the case editor. Deliveries is treated
 * as `area: true` because its value may be rich HTML or free text. */
const CASE_TRANSLATABLE_FIELDS = [
  { key: "title", label: "Título do case" },
  { key: "concept", label: "Conceito (chamada grande)", area: true },
  { key: "cover_alt", label: "Alt da capa (SEO)" },
  { key: "situation", label: "Situação", area: true },
  { key: "solution", label: "Solução", area: true },
  { key: "strategy", label: "Estratégia", area: true },
  { key: "deliveries", label: "Entregas", area: true },
  { key: "results", label: "Resultados", area: true },
];

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

/* ============ LIST WITH DRAG-REORDER ============ */

function SortableCaseRow({ c, onEdit, onRemove, onToggleHidden, onToggleImpl }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const hidden = !!c.is_hidden;
  const inImpl = !!c.is_in_implementation;
  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-4 border ${hidden ? "border-black/20 bg-neutral-100" : "border-black/10 bg-white"} p-3 rounded-md hover:bg-black/[0.02]`} data-testid={`case-row-${c.slug}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-black transition-colors" title="Arrastar para reordenar" data-testid={`case-drag-${c.slug}`}>
        <GripVertical className="w-4 h-4" />
      </button>
      <div className={`w-24 h-16 bg-neutral-200 overflow-hidden rounded relative ${hidden ? "opacity-50" : ""}`}>
        {c.cover && <img src={resolveSrc(c.cover)} alt={c.cover_alt || ""} className={`w-full h-full object-cover ${inImpl ? "grayscale opacity-70" : ""}`} />}
        {hidden && <span className="absolute inset-0 bg-black/40 flex items-center justify-center"><EyeOff className="w-4 h-4 text-white" /></span>}
        {inImpl && !hidden && <span className="absolute top-0.5 left-0.5 text-[8px] font-bold text-white px-1 py-0.5 rounded uppercase" style={{ backgroundColor: "#5B0F1A" }}>impl.</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold truncate ${hidden ? "text-neutral-500" : ""}`}>{c.title}</div>
        <div className="text-xs text-neutral-500 truncate">
          {c.developer} · {c.year} · {c.city}/{c.state}
          {c.featured && <span className="ml-2 text-accent-orange">★ destaque</span>}
          {hidden && <span className="ml-2 text-neutral-500 uppercase tracking-widest text-[10px]">· oculto</span>}
          {inImpl && <span className="ml-2 uppercase tracking-widest text-[10px]" style={{ color: "#5B0F1A" }}>· em implementação</span>}
        </div>
      </div>
      <button onClick={onToggleHidden} data-testid={`case-toggle-hidden-${c.slug}`}
        title={hidden ? "Case oculto — clique para mostrar no site" : "Ocultar do site (mantém no CMS)"}
        className={`text-xs border px-3 py-1.5 rounded transition-colors ${hidden ? "border-black/20 text-neutral-500 hover:bg-black/5" : "border-black/15 hover:bg-black/5"}`}>
        {hidden ? <EyeOff className="w-4 h-4 inline" /> : <Eye className="w-4 h-4 inline" />}
      </button>
      <button onClick={onToggleImpl} data-testid={`case-toggle-impl-${c.slug}`}
        title={inImpl ? "Em implementação — clique para liberar como case público" : "Marcar como 'em implementação' (não clicável no site, selo vinho)"}
        className={`text-xs border px-3 py-1.5 rounded transition-colors ${inImpl ? "text-white" : "border-black/15 hover:bg-black/5"}`}
        style={inImpl ? { backgroundColor: "#5B0F1A", borderColor: "#5B0F1A" } : undefined}>
        <Wrench className="w-4 h-4 inline" />
      </button>
      <button onClick={onEdit} className="text-xs border border-black/15 px-3 py-1.5 rounded hover:bg-black/5 transition-colors" data-testid={`case-edit-${c.slug}`}><Edit className="w-3 h-3 inline" /> Editar</button>
      <button onClick={onRemove} className="text-xs text-red-500 px-3 py-1.5 hover:bg-red-50 rounded transition-colors"><Trash2 className="w-3 h-3 inline" /></button>
    </div>
  );
}

export function CasesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => { setLoading(true); const { data } = await api.get("/content/cases"); setItems(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Excluir este case? Pode ser restaurado pelo histórico.")) return;
    await api.delete(`/admin/cases/${id}`);
    toast.success("Excluído.");
    load();
  };

  const toggleHidden = async (c) => {
    const next = !c.is_hidden;
    // Optimistic UI
    setItems((arr) => arr.map((it) => it.id === c.id ? { ...it, is_hidden: next } : it));
    try {
      await api.put(`/admin/cases/${c.id}`, { ...c, is_hidden: next });
      toast.success(next ? "Case oculto do site." : "Case visível no site.");
    } catch {
      setItems((arr) => arr.map((it) => it.id === c.id ? { ...it, is_hidden: !next } : it));
      toast.error("Não foi possível alterar a visibilidade.");
    }
  };

  const toggleImpl = async (c) => {
    const next = !c.is_in_implementation;
    setItems((arr) => arr.map((it) => it.id === c.id ? { ...it, is_in_implementation: next } : it));
    try {
      await api.put(`/admin/cases/${c.id}`, { ...c, is_in_implementation: next });
      toast.success(next ? "Case marcado como 'em implementação'." : "Case liberado (não é mais 'em implementação').");
    } catch {
      setItems((arr) => arr.map((it) => it.id === c.id ? { ...it, is_in_implementation: !next } : it));
      toast.error("Não foi possível alterar o estado.");
    }
  };

  const handleReorder = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    try {
      await api.post("/admin/cases/reorder", { ids: next.map((i) => i.id) });
      toast.success("Ordem salva.");
    } catch { toast.error("Erro ao salvar ordem."); load(); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="font-display text-4xl">Cases</h1>
          <p className="text-xs text-neutral-500 mt-1">Arraste os cards pelo <b>ícone à esquerda</b> para reordenar. A ordem é salva automaticamente e reflete no site.</p>
        </div>
        <Link to="/admin/cases/new" className="bg-accent-yellow text-black text-sm font-semibold px-4 py-2 rounded-full hover:bg-black hover:text-white transition-colors" data-testid="case-new"><Plus className="w-4 h-4 inline mr-1" /> Novo case</Link>
      </div>
      {loading ? <div className="text-sm text-neutral-500">Carregando…</div> : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2" data-testid="cases-list">
              {items.map((c) => (
                <SortableCaseRow key={c.id} c={c} onEdit={() => navigate(`/admin/cases/${c.id}`)} onRemove={() => remove(c.id)} onToggleHidden={() => toggleHidden(c)} onToggleImpl={() => toggleImpl(c)} />
              ))}
              {!items.length && <div className="text-sm text-neutral-500 text-center py-12">Nenhum case ainda. Use &quot;Importar conteúdo inicial&quot; no dashboard.</div>}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

/* ============ EDITOR ============ */

function MultiCheckboxes({ options = [], value = [], onChange, testid }) {
  const toggle = (opt) => {
    const has = value.includes(opt);
    onChange(has ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  if (!options.length) return <div className="text-xs text-neutral-400">Nenhuma opção. Adicione em <a href="/admin/taxonomies" className="underline">Taxonomias</a>.</div>;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => (
        <label key={opt} className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${value.includes(opt) ? "bg-accent-yellow border-black text-black font-semibold" : "border-black/15 hover:bg-black/5"}`}>
          <input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)} className="sr-only" />
          {opt}
        </label>
      ))}
    </div>
  );
}

function MainTeamEditor({ team = [], onChange, roleOptions = [] }) {
  const upd = (i, k, v) => onChange(team.map((t, j) => j === i ? { ...t, [k]: v } : t));
  const add = () => onChange([...team, { name: "", role: "" }]);
  const remove = (i) => onChange(team.filter((_, j) => j !== i));
  return (
    <div className="space-y-2" data-testid="case-main-team">
      {team.map((t, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center">
          <input value={t.name || ""} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Nome" className="border border-black/15 px-3 py-2 rounded text-sm" data-testid={`team-name-${i}`} />
          <input list={`team-roles-list`} value={t.role || ""} onChange={(e) => upd(i, "role", e.target.value)} placeholder="Função (ex: Direção de Arte)" className="border border-black/15 px-3 py-2 rounded text-sm" data-testid={`team-role-${i}`} />
          <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700 transition-colors"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <datalist id="team-roles-list">
        {roleOptions.map((r) => <option key={r} value={r} />)}
      </datalist>
      <button onClick={add} className="text-xs bg-accent-yellow px-3 py-1.5 rounded-full font-semibold hover:bg-black hover:text-white transition-colors" data-testid="team-add"><Plus className="w-3 h-3 inline" /> Adicionar pessoa</button>
    </div>
  );
}

function QuoteEditor({ quote = {}, onChange }) {
  const upd = (patch) => onChange({ ...quote, ...patch });
  return (
    <div className="border border-accent-orange/30 bg-orange-50/30 rounded-md p-4" data-testid="case-quote-editor">
      <label className="flex items-center gap-2 text-sm mb-3 font-semibold">
        <input type="checkbox" checked={!!quote.enabled} onChange={(e) => upd({ enabled: e.target.checked })} data-testid="case-quote-enabled" />
        Incluir citação do cliente nesta página
      </label>
      {quote.enabled && (
        <div className="space-y-3">
          <textarea value={quote.text || ""} onChange={(e) => upd({ text: e.target.value })} rows={3} placeholder="Depoimento do cliente sobre este case…" className="w-full border border-black/15 px-3 py-2 rounded text-sm" data-testid="case-quote-text" />
          <div className="grid md:grid-cols-2 gap-3">
            <input value={quote.author || ""} onChange={(e) => upd({ author: e.target.value })} placeholder="Nome do autor" className="border border-black/15 px-3 py-2 rounded text-sm" data-testid="case-quote-author" />
            <input value={quote.role || ""} onChange={(e) => upd({ role: e.target.value })} placeholder="Cargo / empresa" className="border border-black/15 px-3 py-2 rounded text-sm" data-testid="case-quote-role" />
          </div>
          <div>
            <label className="text-xs text-neutral-500">Avatar (foto redonda do autor)</label>
            {quote.avatar && <img src={resolveSrc(quote.avatar)} alt="" className="w-12 h-12 rounded-full object-cover my-2" />}
            <DropZone multiple={false} onUploaded={(f) => upd({ avatar: f.url })} />
            <input value={quote.avatar || ""} onChange={(e) => upd({ avatar: e.target.value })} placeholder="ou URL" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-1" />
          </div>
        </div>
      )}
    </div>
  );
}

export function CaseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [item, setItem] = useState(EMPTY_CASE);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Taxonomies from CMS KVs
  const [tax, setTax] = useState({ projects: [], verticals: [], padroes: [], countries: [], team_roles: [] });

  useEffect(() => {
    Promise.all([
      api.get("/content/kv/case_projects").catch(() => ({})),
      api.get("/content/kv/case_categories").catch(() => ({})), // legacy fallback
      api.get("/content/kv/verticals").catch(() => ({})),
      api.get("/content/kv/padroes").catch(() => ({})),
      api.get("/content/kv/countries").catch(() => ({})),
      api.get("/content/kv/team_roles").catch(() => ({})),
    ]).then(([proj, legacy, verticals, padroes, countries, team_roles]) => {
      const projects = Array.isArray(proj?.data) && proj.data.length ? proj.data : (Array.isArray(legacy?.data) ? legacy.data : []);
      setTax({
        projects,
        verticals: Array.isArray(verticals?.data) ? verticals.data : [],
        padroes: Array.isArray(padroes?.data) ? padroes.data : [],
        countries: Array.isArray(countries?.data) ? countries.data : ["Brasil"],
        team_roles: Array.isArray(team_roles?.data) ? team_roles.data : [],
      });
    });
    if (isNew) return;
    api.get(`/content/cases/${id}`).then(({ data }) => setItem({ ...EMPTY_CASE, ...data, quote: { ...EMPTY_CASE.quote, ...(data.quote || {}) } })).finally(() => setLoading(false));
  }, [id, isNew]);

  const upd = (k, v) => setItem((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const data = { ...item };
      ["categories", "services", "secondary_keywords", "verticals"].forEach(k => { if (typeof data[k] === "string") data[k] = data[k].split(",").map(s => s.trim()).filter(Boolean); });
      data.year = Number(data.year);
      data.slug = data.slug || data.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      if (isNew) { await api.post("/admin/cases", data); toast.success("Case criado."); }
      else { await api.put(`/admin/cases/${id}`, data); toast.success("Case atualizado."); }
      navigate("/admin/cases");
    } catch (e) { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500">Carregando…</div>;

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin/cases" className="text-xs text-neutral-500 hover:text-black transition-colors"><ArrowLeft className="w-3 h-3 inline" /> Voltar</Link>
          <h1 className="font-display text-4xl mt-1">{isNew ? "Novo case" : item.title || "Editando case"}</h1>
        </div>
        <button onClick={save} disabled={saving} data-testid="case-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange transition-colors disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-4">
          <Field label="Título" v={item.title} on={(v) => upd("title", v)} testid="case-title" />
          <Field label="Slug (opcional — gerado a partir do título)" v={item.slug} on={(v) => upd("slug", v)} testid="case-slug" />
          <Area label="Conceito / lead" v={item.concept} on={(v) => upd("concept", v)} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Empresa" v={item.developer} on={(v) => upd("developer", v)} />
            <Field label="Direção (Direção geral do projeto)" v={item.director} on={(v) => upd("director", v)} />
            <Field label="Cidade" v={item.city} on={(v) => upd("city", v)} />
            <Field label="Estado (UF)" v={item.state} on={(v) => upd("state", v)} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-neutral-500">País</label>
              <select value={item.country || "Brasil"} onChange={(e) => upd("country", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm bg-white" data-testid="case-country">
                {(tax.countries.length ? tax.countries : ["Brasil"]).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Ano" type="number" v={item.year} on={(v) => upd("year", v)} />
            <Field label="Tipologias" v={item.typologies} on={(v) => upd("typologies", v)} />
            <Field label="Metragens" v={item.sizes} on={(v) => upd("sizes", v)} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Período" v={item.period} on={(v) => upd("period", v)} />
            <Field label="VGV Estimado (ex: R$ 250M)" v={item.vgv_estimado} on={(v) => upd("vgv_estimado", v)} testid="case-vgv" />
            <div>
              <label className="text-xs text-neutral-500">Padrão</label>
              <select value={item.padrao || ""} onChange={(e) => upd("padrao", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm bg-white" data-testid="case-padrao">
                <option value="">— selecione —</option>
                {tax.padroes.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-neutral-500">Tipo de Produto</label>
              <select value={(item.verticals && item.verticals[0]) || ""} onChange={(e) => upd("verticals", e.target.value ? [e.target.value] : [])} className="w-full border border-black/15 px-3 py-2 rounded text-sm bg-white" data-testid="case-vertical">
                <option value="">— selecione —</option>
                {tax.verticals.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-neutral-500 block mb-2">Projeto (marque um ou mais — ex: Branding + Lançamento)</label>
            <MultiCheckboxes options={tax.projects} value={item.categories || []} onChange={(v) => upd("categories", v)} testid="case-projects" />
          </div>

          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!item.featured} onChange={(e) => upd("featured", e.target.checked)} data-testid="case-featured" /> Marcar como case em destaque na Home</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!item.is_hidden} onChange={(e) => upd("is_hidden", e.target.checked)} data-testid="case-hidden" /> <span className="inline-flex items-center gap-1"><span aria-hidden>👁️</span> Esconder do site (mantém no CMS, não aparece publicamente)</span></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!item.is_in_implementation} onChange={(e) => upd("is_in_implementation", e.target.checked)} data-testid="case-in-implementation" /> <span className="inline-flex items-center gap-1"><Wrench className="w-3.5 h-3.5" style={{ color: "#5B0F1A" }} /> Em implementação (mostra selo vinho, thumb com filtro esmaecido, não clicável)</span></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!item.is_classic} onChange={(e) => upd("is_classic", e.target.checked)} data-testid="case-classic" /> <span className="inline-flex items-center gap-1"><span className="text-accent-yellow">👑</span> Clássicos Places (mostra coroa dourada no thumb)</span></label>

          <div>
            <label className="text-xs text-neutral-500 block mb-1">Cor de fundo do detalhe (opcional)</label>
            <div className="flex items-center gap-2">
              <input type="color" value={item.background_color || "#ffffff"} onChange={(e) => upd("background_color", e.target.value)} data-testid="case-bg-color" className="w-12 h-9 border border-black/15 rounded cursor-pointer" />
              <input type="text" value={item.background_color || ""} onChange={(e) => upd("background_color", e.target.value)} placeholder="#ffffff ou vazio" className="flex-1 border border-black/15 rounded px-3 py-2 text-sm font-mono" />
              {item.background_color && <button onClick={() => upd("background_color", "")} className="text-xs text-red-500 hover:text-red-700">Limpar</button>}
            </div>
          </div>

          <hr className="my-2" />
          <div className="flex items-center justify-between">
            <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700">Ficha do produto</div>
            <div className="text-[10px] text-neutral-400 flex items-center gap-1"><Palette className="w-3 h-3" /> Cores por seção editáveis</div>
          </div>
          <SectionStyleControls styles={item.section_styles || {}} onChange={(v) => upd("section_styles", v)} />
          <RichArea label="Situação" v={item.situation} on={(v) => upd("situation", v)} testid="case-situation" />
          <RichArea label="Solução" v={item.solution} on={(v) => upd("solution", v)} testid="case-solution" />
          <RichArea label="Estratégia" v={item.strategy} on={(v) => upd("strategy", v)} testid="case-strategy" />
          <RichArea label="Entregas" v={item.deliveries} on={(v) => upd("deliveries", v)} testid="case-deliveries"
            hint="Uma entrega por linha OU separadas por vírgula/ponto e vírgula — cada item vira um bullet no site." />
          <RichArea label="Resultados" v={item.results} on={(v) => upd("results", v)} testid="case-results" />

          {/* Tradução automática EN/ES — o admin edita PT nos campos acima; o botão
              "Traduzir com IA" pré-preenche EN/ES em uma chamada e permite ajuste manual. */}
          <TranslationTabs
            value={item.translations || {}}
            onChange={(v) => upd("translations", v)}
            sourceFields={{
              title: item.title, concept: item.concept, cover_alt: item.cover_alt,
              situation: item.situation, solution: item.solution, strategy: item.strategy,
              deliveries: item.deliveries, results: item.results,
            }}
            fields={CASE_TRANSLATABLE_FIELDS}
            context="case"
            testidPrefix="case-tt"
          />

          {/* Equipe principal */}
          <div>
            <label className="text-xs text-neutral-500 block mb-1">Equipe Principal (aparece na ficha, abaixo de Direção)</label>
            <MainTeamEditor team={item.main_team || []} onChange={(v) => upd("main_team", v)} roleOptions={tax.team_roles} />
          </div>

          {/* Citação do cliente */}
          <QuoteEditor quote={item.quote || {}} onChange={(v) => upd("quote", v)} />

          {/* Content Blocks — o coração da página */}
          <hr className="my-2" />
          <div>
            <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-2">Conteúdo visual do case</div>
            <p className="text-xs text-neutral-500 mb-4">Adicione blocos de <b>imagem</b>, <b>vídeo</b>, <b>dupla</b> (2 mídias lado a lado) ou <b>galeria</b> (múltiplas em grid). Arraste pelo ícone à esquerda para reordenar. Cada bloco tem um controle de <b>espaço abaixo</b> em pixels.</p>
            <ContentBlocksEditor blocks={item.content_blocks || []} onChange={(v) => upd("content_blocks", v)} testidPrefix="case-content-blocks" />
          </div>

          {/* ============ SEO (Advanced) ============ */}
          <div className="border border-accent-orange/30 bg-orange-50/40 rounded-md p-4 mt-4" data-testid="case-seo-section">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] uppercase tracking-widest bg-accent-orange text-white px-2 py-0.5 rounded">SEO Avançado</span>
              <span className="text-xs text-neutral-500">Sobrescreve os padrões globais em /admin/seo</span>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-neutral-500 flex justify-between"><span>Meta title (50-60 caracteres)</span><span className="font-mono">{(item.meta_title || "").length}/60</span></label>
                <input value={item.meta_title || ""} onChange={(e) => upd("meta_title", e.target.value)} data-testid="case-meta-title" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-neutral-500 flex justify-between"><span>Meta description (155-160 caracteres)</span><span className="font-mono">{(item.meta_description || "").length}/160</span></label>
                <textarea value={item.meta_description || ""} onChange={(e) => upd("meta_description", e.target.value)} rows={2} data-testid="case-meta-desc" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-neutral-500">Palavra-chave principal</label>
                  <input value={item.main_keyword || ""} onChange={(e) => upd("main_keyword", e.target.value)} data-testid="case-main-kw" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-neutral-500">Palavras-chave secundárias (vírgulas)</label>
                  <input value={Array.isArray(item.secondary_keywords) ? item.secondary_keywords.join(", ") : (item.secondary_keywords || "")} onChange={(e) => upd("secondary_keywords", e.target.value)} data-testid="case-secondary-kw" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-neutral-500">Imagem OG (URL 1200×630 — usa a Capa se vazio)</label>
                <input value={item.og_image || ""} onChange={(e) => upd("og_image", e.target.value)} data-testid="case-og-image" className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              </div>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!item.noindex} onChange={(e) => upd("noindex", e.target.checked)} data-testid="case-noindex" /> Marcar este case como <code>noindex</code></label>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Capa</div>
            {item.cover && <img src={resolveSrc(item.cover)} alt={item.cover_alt || ""} className="w-full aspect-video object-cover rounded mb-2" />}
            <DropZone multiple={false} onUploaded={(f) => upd("cover", f.url)} />
            <input value={item.cover || ""} onChange={(e) => upd("cover", e.target.value)} placeholder="ou URL" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-2" data-testid="case-cover-url" />
            <label className="text-[10px] text-neutral-500 block mt-2">Alt da capa (acessibilidade + SEO)</label>
            <input value={item.cover_alt || ""} onChange={(e) => upd("cover_alt", e.target.value)} placeholder="Descrição da imagem" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded" data-testid="case-cover-alt" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2 flex justify-between items-center"><span>Galeria legada · ordenável</span><span className="text-[10px] text-neutral-400">{(item.gallery || []).length} fotos</span></div>
            <p className="text-[10px] text-neutral-400 mb-2">Legada — hoje a galeria vive dentro dos <b>blocos de conteúdo</b>. Este campo continua funcionando para cases antigos.</p>
            <SortableImages items={item.gallery || []} onChange={(arr) => upd("gallery", arr)} />
            <div className="mt-3"><DropZone multiple onUploaded={(arr) => upd("gallery", [...(item.gallery || []), ...arr.map((a) => a.url)])} /></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Vídeo Principal</div>
            <p className="text-[10px] text-neutral-400 mb-2">Aparece no topo da página do case (16:9). Aceita YouTube, Vimeo ou upload direto.</p>
            <input value={item.video_url || ""} onChange={(e) => upd("video_url", e.target.value)} placeholder="URL YouTube/Vimeo ou vídeo direto" data-testid="case-video-url" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mb-2 font-mono" />
            <DropZone multiple={false} accept={{ "video/*": [".mp4", ".webm", ".mov", ".m4v"] }} hint="Vídeos · até 100MB" onUploaded={(f) => upd("video_url", f.url)} />
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", placeholder, testid }) {
  return (<div><label className="text-xs text-neutral-500">{label}</label><input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={testid} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>);
}
function Area({ label, v, on }) {
  return (<div><label className="text-xs text-neutral-500">{label}</label><textarea value={v ?? ""} onChange={(e) => on(e.target.value)} rows={3} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>);
}

/**
 * RichArea — rich text editor per case field (Situação, Solução, Estratégia, Entregas, Resultados).
 * Backward compatible: legacy plain-text values render fine (TinyMCE wraps them in <p>).
 * The public case detail sanitizes with DOMPurify so writing rich HTML is safe.
 */
function RichArea({ label, v, on, testid, hint }) {
  return (
    <div data-testid={testid ? `${testid}-wrapper` : undefined}>
      <label className="text-xs text-neutral-500 flex justify-between items-baseline">
        <span>{label}</span>
        {hint && <span className="text-[10px] text-neutral-400 font-normal">{hint}</span>}
      </label>
      <div className="border border-black/15 rounded overflow-hidden">
        <TinyEditor
          tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@7/tinymce.min.js"
          value={v || ""}
          onEditorChange={(html) => on(html)}
          init={{
            height: 200, menubar: false,
            plugins: "lists link autolink quickbars code",
            toolbar: "undo redo | fontsize | bold italic underline | forecolor backcolor | bullist numlist | link removeformat | code",
            fontsize_formats: "10px 12px 14px 15px 16px 18px 20px 24px 32px",
            content_style: "body{font-family:Poppins,sans-serif;font-size:15px;line-height:1.6;color:#171717;} p{margin:0 0 .5em 0;}",
            license_key: "gpl",
          }}
        />
      </div>
    </div>
  );
}

const SECTION_STYLE_FIELDS = [
  { key: "situation_color", label: 'Cor do título "Situação"' },
  { key: "solution_color", label: 'Cor do título "Solução"' },
  { key: "strategy_color", label: 'Cor do título "Estratégia"' },
  { key: "deliveries_color", label: 'Cor do título "Entregas"' },
  { key: "deliveries_bullet_color", label: 'Cor dos bullets de "Entregas"' },
  { key: "results_color", label: 'Cor do título "Resultados"' },
];

function SectionStyleControls({ styles = {}, onChange }) {
  const upd = (k, v) => onChange({ ...styles, [k]: v });
  return (
    <div className="border border-accent-orange/25 bg-orange-50/30 rounded-md p-4" data-testid="case-section-styles">
      <div className="text-[10px] uppercase tracking-widest text-accent-orange font-bold mb-3">Cores das seções (Situação, Solução, Estratégia, Entregas)</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {SECTION_STYLE_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[10px] text-neutral-500 block mb-1">{f.label}</label>
            <div className="flex items-center gap-1.5">
              <input type="color" value={styles[f.key] || "#FF5E00"} onChange={(e) => upd(f.key, e.target.value)}
                data-testid={`case-style-${f.key}`}
                className="w-9 h-8 border border-black/15 rounded cursor-pointer" />
              <input type="text" value={styles[f.key] || ""} onChange={(e) => upd(f.key, e.target.value)}
                placeholder="#FF5E00"
                className="flex-1 border border-black/15 rounded px-2 py-1.5 text-xs font-mono" />
              {styles[f.key] && styles[f.key] !== "#FF5E00" && (
                <button onClick={() => upd(f.key, "")} className="text-[10px] text-red-500 hover:text-red-700 px-1" title="Reset">×</button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-neutral-400 mt-3">Padrão: <code>#FF5E00</code> (laranja Places). Deixe vazio para usar o padrão global.</div>
    </div>
  );
}
